import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import AddQuickReplyModal from "./add-modal";
import { loadQuickReplies, saveQuickReplies } from "./store";
import { QUICK_REPLY_CATEGORIES, QuickReply } from "./types";

const CATEGORY_TABS = [{ value: "all", label: "All" }, ...QUICK_REPLY_CATEGORIES];

const SendQuickReplyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  recipientName?: string;
  onSelect: (reply: QuickReply) => void;
}> = ({ isOpen, onClose, recipientName, onSelect }) => {
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReplies(loadQuickReplies().filter((r) => r.status === "Active"));
      setActiveCategory("all");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filtered = activeCategory === "all" ? replies : replies.filter((r) => r.categoryValue === activeCategory);

  const handleAdded = (reply: QuickReply) => {
    const all = [reply, ...loadQuickReplies()];
    saveQuickReplies(all);
    setReplies(all.filter((r) => r.status === "Active"));
    setShowAddModal(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
          <div className="p-6 pb-4 shrink-0">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send a Quick Reply</h2>
              <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Send a quick reply to{recipientName ? <span className="underline"> {recipientName}</span> : null}
            </p>
          </div>

          <div className="px-6 shrink-0">
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-1 overflow-x-auto">
              {CATEGORY_TABS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setActiveCategory(c.value)}
                  className={`flex-1 whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeCategory === c.value ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm" : "text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500 text-center py-6">No quick replies in this category yet.</p>
            ) : (
              filtered.map((r) => (
                <button
                  key={r.id}
                  onClick={() => {
                    onSelect(r);
                    onClose();
                  }}
                  className="w-full text-left px-4 py-3 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors text-sm text-gray-800 dark:text-gray-200"
                >
                  “{r.message}”
                </button>
              ))
            )}
          </div>

          <div className="p-6 pt-2 flex flex-col gap-3 shrink-0">
            <button
              onClick={() => setShowAddModal(true)}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Add New
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <AddQuickReplyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        categories={QUICK_REPLY_CATEGORIES}
        onAdd={handleAdded}
      />
    </>
  );
};

export default SendQuickReplyModal;
